/* eslint-disable max-lines-per-function */
import { track } from "jiffy-analytics";
import initKlarnaCreditBanner from "components/jiffy/klarna-credit-banner/klarna-credit-banner";

const PLACEMENT_SELECTOR = "klarna-placement";
const INTERSECTION_ROOT_MARGIN = "600px";
// React cart hydration on slow networks can mount <klarna-placement> well after
// window.load. 30s is a generous ceiling that still bounds the dangling MO if
// the placement never appears (e.g. financing disabled, empty cart).
const PLACEMENT_WAIT_GRACE_MS = 30000;

const $getKlarnaPlacement = () => document.querySelector(PLACEMENT_SELECTOR);

const trackKlarnaLearnMoreClicked = () => {
  const { context } = $getKlarnaPlacement()?.dataset || {};
  track("payment_solution_learn_more_clicked", {
    payment_solution: "klarna",
    context
  });
};

const loadSDK = (clientId, src) => {
  const script = document.createElement("script");
  script.async = 1;
  script.src = src;
  script.setAttribute("data-client-id", clientId);

  document.body.appendChild(script);
};

const mobileModalEnhancement = () => {
  let scrollPosition = null;

  const callback = mutationsList => {
    mutationsList.forEach(mutation => {
      if (mutation.type !== "childList") return;
      if (!(mutation.addedNodes instanceof NodeList)) return;

      const nodesArray = [...mutation.addedNodes];
      if (!nodesArray.length) {
        if (scrollPosition !== null) {
          document.documentElement.classList.remove("locked-mobile");
          window.scrollTo({ top: scrollPosition });
          scrollPosition = null;
        }

        return;
      }

      const klarnaIframe = nodesArray.find(({ tagName }) => tagName === "KLARNA-OSM-INTERSTITIAL");
      if (klarnaIframe) {
        scrollPosition = window.scrollY;
        document.documentElement.style.setProperty(
          "--window-inner-height",
          `${window.innerHeight}px`
        );
        document.documentElement.classList.add("locked-mobile");
        trackKlarnaLearnMoreClicked();
      }
    });
  };

  const observer = new MutationObserver(callback);
  observer.observe(document.body, { attributes: false, childList: true, subtree: false });
};

const bootstrapLazyLoader = () => {
  const $klarnaConfig = document.querySelector("#klarna-services-config");
  if (!$klarnaConfig) return;

  const { clientId, src } = $klarnaConfig.dataset;
  let loaded = false;
  const observed = new WeakSet();
  const intersectionObservers = [];
  let placementObserver = null;

  const teardown = () => {
    intersectionObservers.forEach(obs => obs.disconnect());
    intersectionObservers.length = 0;
    placementObserver?.disconnect();
  };

  const triggerLoad = () => {
    if (loaded) return;
    loaded = true;
    teardown();
    loadSDK(clientId, src);
    mobileModalEnhancement();
    initKlarnaCreditBanner();
  };

  const hasIntersectionObserver = typeof window.IntersectionObserver === "function";
  const hasMutationObserver = typeof window.MutationObserver === "function";

  // Fallback for ancient browsers — load immediately if observers are unavailable.
  if (!hasIntersectionObserver) {
    triggerLoad();
    return;
  }

  const observePlacement = $placement => {
    if (observed.has($placement)) return;
    observed.add($placement);
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        triggerLoad();
      },
      { root: null, rootMargin: INTERSECTION_ROOT_MARGIN, threshold: 0 }
    );
    observer.observe($placement);
    intersectionObservers.push(observer);
  };

  const observeAllPlacements = () => {
    document.querySelectorAll(PLACEMENT_SELECTOR).forEach(observePlacement);
  };

  // Server-rendered placements (e.g. the hidden BNPL modal banner on /cart/buy)
  // are observed at parse time. Hidden elements never trigger IO; when the user
  // opens the modal they become visible and IO fires — at which point we want
  // the SDK loaded regardless of which placement scrolled in first.
  observeAllPlacements();

  // Watch for React-rendered placements (KlarnaSection.vue.tsx on /cart/buy)
  // that mount after this script parses. MO stays installed until either a
  // placement intersects, or the grace period expires.
  if (!hasMutationObserver) return;

  placementObserver = new window.MutationObserver(observeAllPlacements);
  placementObserver.observe(document.body, { childList: true, subtree: true });

  const stopWaiting = () => {
    setTimeout(() => placementObserver?.disconnect(), PLACEMENT_WAIT_GRACE_MS);
  };
  if (document.readyState === "complete") {
    stopWaiting();
  } else {
    window.addEventListener("load", stopWaiting, { once: true });
  }
};

bootstrapLazyLoader();
